import { MessageRole } from '../../clients/IsaConversationClient/generated/ConversationMessageClient/types';
import { validateProps } from '../../utils';
import { isValidBase } from '../BaseEntity';
export { MessageRole };

/**
 * Domain entity for a wix.individual_scheduling_assistant.v1.conversation_message.
 *
 * Discriminated union on `role` — mirrors the proto's role-conditional required fields:
 * - USER / ASSISTANT / SYSTEM / TOOL_RESULT / THINKING → `content` is required
 * - TOOL_USE                                           → `toolName` + `toolInput` (JSON) are required
 *
 * `metadata.toolUseId` links TOOL_USE ↔ TOOL_RESULT pairs across the conversation.
 * THINKING rows carry the LLM-authored "doing X" label for the UI as `content`.
 */
/**
 * Typed metadata persisted on IsaConversationMessage rows. The union of all fields — each row
 * uses only the subset that applies to its role:
 *
 * - `toolUseId`   — TOOL_USE / TOOL_RESULT / THINKING rows. Links rows of one assistant turn.
 * - `references`  — USER rows. @-mention references attached by the mobile client.
 * - `iterationId` — Per-turn ID generated at the start of each agentic-turn gateway call (currently
 *                   `Date.now().toString()`). Shared by every row the turn persists (ASSISTANT +
 *                   its TOOL_USE/THINKING rows + the TOOL_RESULT rows). orion-live groups by this.
 * - `reasoning`   — TOOL_USE rows. The LLM-stated rationale for invoking the tool, extracted from
 *                   tool input before persistence. Monitoring-only — tool implementations never
 *                   see this value, and it does not flow back to the LLM on subsequent turns.
 */
/**
 * Attachment metadata stored on USER message rows. Mobile uploads to genie-upload first, then
 * sends { fileName, mimeType, url, size } in the body of the streamed-message request.
 *
 * Two server-side policies apply:
 *
 * 1. Size cap — when `size` is present, the handler enforces it against
 *    `MAX_ATTACHMENT_SIZE_BYTES` (5 MB, the tightest underlying-provider bound — Anthropic's
 *    per-image limit) and returns HTTP 413 before opening any gateway stream.
 *
 *    VIDEO is the one exception, and the reason is this cap's own rationale: it is a bound on what
 *    the model READS, and no video is ever sent to a model — every mapper resolves `video/*` to a
 *    text note and the URL alone rides the metadata for the agent to hand to a connector. So video
 *    is gated on `MAX_VIDEO_ATTACHMENT_SIZE_BYTES` instead; use `maxAttachmentSizeBytes(mimeType)`
 *    rather than the constant directly, or a 40 MB clip is refused by a limit that does not apply
 *    to it. `size` is
 *    optional because older mobile builds predate the field; when it's missing,
 *    `classifyGatewayError` (in streaming-agentic-turn) is the fallback — it sniffs known
 *    size-rejection markers from provider error messages and translates the SSE event into
 *    `AssistantStreamErrorCode.ATTACHMENT_TOO_LARGE`.
 *
 * 2. History stripping — `entitiesToGatewayMessages` emits media blocks only for USER rows
 *    not yet followed by a final ASSISTANT row. Once the assistant finalizes a reply to a
 *    turn, that turn's media drops from subsequent gateway calls (the model already "saw" it
 *    in its prior response — re-sending bytes on every iteration would just bloat the
 *    request). Persistence is untouched; this only filters LLM-facing emission.
 *    ASSISTANT_STEP doesn't count as "answered" so mid-tool-loop iterations still see the
 *    open turn's media.
 */

/**
 * Per-iteration LLM spend. `microcents` is in 1e-8 USD. The token split carries the per-call
 * cost drivers: `inputTokens` bill at full rate, `cacheReadTokens` at ~0.1x, `cacheCreationTokens`
 * at ~1.25x. Thought/tool-use tokens are omitted (the assistant has no native extended thinking).
 */

/**
 * Marker persisted on the synthetic ASSISTANT row that announces a newly-hired agent (see
 * ManageAgent `create`). Clients that recognize it render the row as the branded "agent joined"
 * orbit card (the same genome-assemble motion as the Agents hero / connecting splash), which
 * self-animates on mount; the row's plain-text `content` is the graceful fallback for any client
 * that doesn't. The row is stripped from the LLM-facing view (see gatewayMapping) so the model
 * still performs its own mandated "meet {name}" introduction.
 */

/**
 * Which setup milestone a {@link MilestoneCardMetadata} celebrates — drives the client's copy and,
 * for `plan`, its icon + eyebrow label. `workspace`/`dashboard`/`scan` render the Figma bubble shell
 * with fixed localized copy per kind; `plan` keeps the compact badge+name layout.
 */

/**
 * Present only on a synthetic ASSISTANT row that celebrates a setup milestone — a workspace being
 * created, the home dashboard being built, or the first plan/routine going live. Its presence is the
 * client's signal to render a milestone card (a lighter sibling of the agent-join orbit, see
 * {@link AgentCardMetadata}). Like the agent card, the row is stripped from the LLM-facing view (see
 * gatewayMapping) so the model never treats its own celebratory marker as a message to respond to.
 * The client composes the localized label from `kind`; `name` / `subtitle` fill in the specifics.
 */

/** Why the agent gave credits away — drives the card's copy and its tone. */

/**
 * Present only on a synthetic ASSISTANT row announcing that the agent gave the user credits from its
 * goodwill allowance (see the GrantGoodwillCredits tool). Its presence is the client's signal to
 * render the credit-gift card — a confetti moment for `appreciation`, a quieter make-good for
 * `reimbursement`. Like the agent and milestone cards, the row is stripped from the LLM-facing view
 * so the model never reads its own celebratory marker as something to reply to.
 *
 * `credits` is the number that ACTUALLY landed, which the server may have clamped below what the
 * agent asked for. Clients render this field and nothing else — a card that congratulates the user
 * on credits they did not receive is worse than no card, and it is the exact failure a
 * separately-authored piece of copy would eventually produce.
 *
 * The two kinds share a shape but not a mood, and the difference is not decorative: confetti over an
 * apology reads as celebrating the user's bad day.
 */

/**
 * Present on an ASSISTANT reply whose turn minted a connector OAuth link (ConnectApp), on
 * card-capable channels (app/web). Clients render a connect card — the app's logo tile, name, and a
 * Connect button opening `connectUrl` — under the reply text, which deliberately carries no raw
 * connect URL. The card is LIVE: clients derive its state from their connectors data (connected →
 * success surface) and from `mintedAt` (a stale un-connected link → offer to re-mint). Text-only
 * channels (WhatsApp/SMS/email/external agents) never get this marker; the reply carries the link
 * as text there instead.
 */
/**
 * The "running low on credits" marker. Like {@link ConnectorCardMetadata} it RIDES the reply itself:
 * clients render the card UNDER the reply text, so the assistant still answers and the nudge sits
 * beneath the answer.
 *
 * Deliberately not a modal on any surface. A prompt that covers the screen at the moment something
 * worked interrupts the very thing it is congratulating; in the thread it costs nothing to scroll past.
 */
/**
 * WHICH credits nudge this card is. The server decides; clients pick copy and button order from it.
 *
 * Absent means `low` — the only variant that existed when the first clients shipped, and the natives
 * ride a later release train than the server. A client that has never heard of a variant must keep
 * rendering the card it already knows rather than blanking, so this is read with a default and never
 * switched on exhaustively.
 */

/**
 * The value family an opportunity belongs to — the chip the client draws, colour + icon coded so it
 * is scannable before a word is read. The maestro picks exactly ONE per opportunity, tagging by the
 * dominant outcome the owner cares about: an opportunity serving two families (self-serve booking is
 * both `new-clients` and `time-saved`) gets the one that is the point.
 *
 * Nine business families then seven personal ones. Context narrows the choice — a business workspace
 * surfaces the business families, a personal workspace the personal ones — but the union is one flat
 * set because a workspace is not typed and the split is a matter of what fits, not of what is legal.
 * The sub-topics that define each family live in the authoring prompt, not here: code only ever needs
 * to know which ids exist, so an unrecognized one can fail closed.
 */

/**
 * The opportunity widget over chat — an OPPORTUNITY-status goal put in front of the owner at a
 * moment that earns it (day 0, after the agents' morning meeting, or when a goal completes), rather
 * than left behind the Workspace tab.
 *
 * Three parts, matching how a goal is structured: the {@link ChipFamily} chip (the value family),
 * the win (the value statement, which IS the goal's title), and the how (the mechanism the agent
 * tasks deliver). `slug` is what a CTA resolves back to.
 *
 * Rides the assistant's reply like `connectorCard` and `choices` rather than being a synthetic
 * presentation row: the conversational lead-in is the model's own text, so this must stay out of
 * {@link isPresentationCardRow} or stripping the row would take the lead-in with it. The two CTAs
 * ("Set it up" / "Not now") are ordinary `choices` on the same row — this card carries content, not
 * intent.
 */

/**
 * Quoted-reply reference persisted on a row whose sender replied to another message. At least one
 * of the two ids is present: a WhatsApp-originated reply always carries `waMessageId` (the target's
 * wamid; `messageId`/`preview`/`sender` fill in when the target resolved to a row in our
 * conversation), while an in-app reply carries `messageId` directly (no wamid exists). Targets with
 * neither a preview nor a resolvable row still render a generic "replied to a message" quote.
 */

/**
 * Structured ask stamped on an ASSISTANT row that offers the user an enumerable set of options
 * (the OfferChoices tool). Clients render `options` as tappable choice chips under the message;
 * tapping one sends a reply carrying a {@link ChoiceAnswer} that echoes `askId` and the tapped
 * option's `id`. The chips are an input affordance, not a constraint — the user can always answer
 * in free text instead.
 */

/**
 * The user's tap on a choice chip, stamped on the USER reply row. `askId` names the
 * {@link MessageChoices} ask being answered; `optionId` is the tapped option's `id`.
 */

/**
 * One reaction on a message. `by` identifies the reactor: the literal 'user' (the site owner in
 * their own chat), an agent id (agent-authored reaction), or a WhatsApp member's waId (group
 * reactions arriving via the channels pipeline). One entry per (emoji, by) — re-reacting with the
 * same emoji toggles the entry off. Stored under `metadata.reactions`, the ONLY path the
 * conversation-message service's UpdateConversationMessage accepts (annotation-only contract).
 */

/**
 * What arrived when an inbound message could not be turned into anything readable. `reason`
 * separates "nothing was in it" (a forward or interactive tap) from the media hops that failed on
 * our side — the distinction the user-facing copy and any triage both depend on.
 */

/**
 * Cap on {@link IsaConversationMessageMetadata.systemErrorReason}.
 *
 * Generous on purpose: a truncated cause is often a useless one. Provider errors bury the part that
 * identifies the failure behind a preamble, and a gRPC status line with a nested upstream message
 * runs long before it says anything specific — cutting at a few hundred characters keeps the
 * boilerplate and drops the answer, which is the failure mode this field exists to end.
 *
 * The cost of the larger cap is bounded: the key is in METADATA_OMIT_FROM_LLM, so it never reaches
 * a prompt and never costs a token no matter how long it runs. It rides one row per failed turn —
 * rare by construction — read by monitoring rather than by the agentic loop.
 */
export const SYSTEM_ERROR_REASON_MAX_LENGTH = 3000;
/**
 * Read the agent-card marker off a message's metadata, returning a validated {@link AgentCardMetadata}
 * or `null` when the row is not an agent-card row. Shared by every client renderer so the "is this an
 * agent-join card?" check stays identical across web and the native bridges.
 */
export function readAgentCard(metadata) {
  const card = metadata == null ? void 0 : metadata.agentCard;
  if (card && typeof card.name === 'string' && card.name.trim().length > 0) {
    return {
      ...(typeof card.id === 'string' && card.id.trim() ? {
        id: card.id
      } : {}),
      name: card.name,
      ...(typeof card.title === 'string' && card.title.trim() ? {
        title: card.title
      } : {})
    };
  }
  return null;
}
const MILESTONE_CARD_KINDS = ['workspace', 'dashboard', 'plan', 'scan', 'phone'];

/**
 * Read the milestone-card marker off a message's metadata, returning a validated
 * {@link MilestoneCardMetadata} or `null` when the row is not a milestone-card row. Shared by every
 * client renderer so the "is this a milestone card, and of which kind?" check stays identical across
 * web and the native bridges. Guards `kind` against the known set so an unknown/forward kind renders
 * nothing rather than a broken card.
 */
export function readMilestoneCard(metadata) {
  const card = metadata == null ? void 0 : metadata.milestoneCard;
  if (!card || !MILESTONE_CARD_KINDS.includes(card.kind)) {
    return null;
  }
  return {
    kind: card.kind,
    ...(typeof card.name === 'string' && card.name.trim() ? {
      name: card.name
    } : {}),
    ...(typeof card.subtitle === 'string' && card.subtitle.trim() ? {
      subtitle: card.subtitle
    } : {}),
    ...(card.nav ? {
      nav: card.nav
    } : {})
  };
}

/**
 * Read the credits-card marker off a message's metadata. Shared by every client renderer AND by the
 * harness that stamps it, so "does this reply carry a credits card?" stays one answer.
 *
 * Fail-closed on a non-positive `cap`: a card that cannot draw a meter renders nothing rather than a
 * bar pinned at zero, which would read as "you have no credits" — the opposite of this card's message.
 * Numbers are coerced from strings because harness metadata is often a `map<string,string>`.
 */
export function readCreditsCard(metadata) {
  const card = metadata == null ? void 0 : metadata.creditsCard;
  if (!card) {
    return null;
  }
  const toInt = value => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.trunc(value);
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Math.trunc(Number(value));
    }
    return null;
  };
  const cap = toInt(card.cap);
  const used = toInt(card.used);
  if (cap === null || cap <= 0 || used === null) {
    return null;
  }
  // An UNRECOGNIZED variant is dropped rather than passed through, so what this returns always holds a
  // value the renderers can switch on — and dropping it lands on the same `low` default a missing one
  // does, which is the tolerant reading a client meeting a newer server needs. A recognized `low` is
  // likewise left off: it is the default, and omitting it keeps the payload byte-identical to what
  // clients predating the field already render.
  const variant = card.variant === 'renewed' ? 'renewed' : undefined;
  return {
    used: Math.max(0, used),
    cap,
    ...(typeof card.successLine === 'string' && card.successLine.trim() ? {
      successLine: card.successLine
    } : {}),
    ...(variant ? {
      variant
    } : {})
  };
}

/**
 * True for a synthetic ASSISTANT row that exists only to make a client draw something — the
 * agent-join orbit, a setup milestone, a credit gift. These rows carry celebratory placeholder text
 * (`✦ Nia joined your team`) that is a caption, not a message: the model must never see one in its
 * history, or it treats its own marker as a turn already taken and skips the introduction / thanks /
 * apology it was supposed to write itself.
 *
 * ONE predicate, used by every provider mapping. It is a shared function rather than a repeated
 * `metadata?.agentCard || metadata?.milestoneCard` because that literal WAS repeated — and had
 * already drifted: only the gateway mapping stripped these rows, while the Anthropic, Gemini and
 * OpenAI paths passed them straight to the model. A new card kind then has to be added in one place
 * to be handled everywhere, which is the only version of this that stays true.
 */
export function isPresentationCardRow(row) {
  const metadata = row.metadata;
  return Boolean((metadata == null ? void 0 : metadata.agentCard) || (metadata == null ? void 0 : metadata.milestoneCard) || (metadata == null ? void 0 : metadata.creditGiftCard));
}
const CREDIT_GIFT_KINDS = ['appreciation', 'reimbursement'];

/**
 * Read the credit-gift marker off a message's metadata, returning a validated
 * {@link CreditGiftCardMetadata} or `null` when the row is not a gift row. Shared by every client
 * renderer so the check stays identical across web and the native bridges.
 *
 * Guards `credits` hard — a positive, finite integer or nothing. This card tells a user how many
 * credits they were given, so a malformed marker rendering as "You received 0 credits", "NaN
 * credits" or a fractional count is a specific, embarrassing failure that a lenient parse would
 * produce and a strict one simply cannot. Rendering nothing is always the better fallback: the
 * agent's own reply already said it in words.
 */
export function readCreditGiftCard(metadata) {
  const card = metadata == null ? void 0 : metadata.creditGiftCard;
  if (!card || !CREDIT_GIFT_KINDS.includes(card.kind)) {
    return null;
  }
  const credits = typeof card.credits === 'number' && Number.isSafeInteger(card.credits) && card.credits > 0 ? card.credits : null;
  if (credits === null) {
    return null;
  }
  return {
    kind: card.kind,
    credits,
    ...(typeof card.note === 'string' && card.note.trim() ? {
      note: card.note.trim()
    } : {})
  };
}

/**
 * Read the connector-card marker off a message's metadata, returning a validated
 * {@link ConnectorCardMetadata} or `null` when the reply carries none. Shared by every client
 * renderer so the "does this reply carry a connect card?" check stays identical across web and the
 * native bridges. A card missing its required fields (toolkit/name/connectUrl) renders nothing
 * rather than a broken card, and `connectUrl` must be https so a corrupted marker can't become a
 * tappable non-web URL.
 */
export function readConnectorCard(metadata) {
  const card = metadata == null ? void 0 : metadata.connectorCard;
  if (!card || typeof card.toolkit !== 'string' || !card.toolkit.trim() || typeof card.name !== 'string' || !card.name.trim() || typeof card.connectUrl !== 'string' || !/^https:\/\/\S+$/i.test(card.connectUrl)) {
    return null;
  }
  return {
    toolkit: card.toolkit,
    name: card.name,
    connectUrl: card.connectUrl,
    ...(typeof card.logoUrl === 'string' && /^https?:\/\/\S+$/i.test(card.logoUrl) ? {
      logoUrl: card.logoUrl
    } : {}),
    ...(typeof card.mintedAt === 'string' && card.mintedAt.trim() ? {
      mintedAt: card.mintedAt
    } : {})
  };
}
/**
 * Every {@link ChipFamily} id, business families first. This is the ONE authoritative list in code —
 * the authoring side deliberately does not repeat it: the `using-goals` skill enumerates the families
 * for the model in prose (with what each covers, which an enum cannot carry), and an id that never
 * appears here is dropped at render by {@link readOpportunityCard}. Exported for any consumer that
 * needs to enumerate or validate the set rather than declaring its own copy.
 */
export const CHIP_FAMILY_IDS = ['revenue', 'new-clients', 'retention', 'client-results', 'get-paid', 'staffing', 'time-saved', 'reputation', 'launch', 'wellbeing', 'habits', 'learning', 'relationships', 'money', 'in-control', 'experiences'];

/**
 * Read the opportunity-card marker off a message's metadata, returning a validated
 * {@link OpportunityCardMetadata} or `null` when the reply carries none. Shared by every client
 * renderer AND by the harness that stamps it, so "does this reply carry an opportunity widget?" stays
 * one answer.
 *
 * Requires both `slug` and `title`. A card without a slug has CTAs that cannot resolve to a goal, and
 * one without a title has nothing to say — either way the assistant's own reply already made the
 * suggestion in words, so rendering nothing is strictly better than rendering a widget that misleads
 * or does nothing when tapped.
 *
 * An unrecognized `value` drops just that field. The chip is decoration on top of a card that still
 * reads correctly without it, and a forward chip family from a newer server must not blank the whole
 * widget — the opposite trade-off from the two required fields above.
 */
export function readOpportunityCard(metadata) {
  const card = metadata == null ? void 0 : metadata.opportunityCard;
  if (!card || typeof card.slug !== 'string' || !card.slug.trim() || typeof card.title !== 'string' || !card.title.trim()) {
    return null;
  }
  return {
    slug: card.slug.trim(),
    title: card.title.trim(),
    ...(typeof card.value === 'string' && CHIP_FAMILY_IDS.includes(card.value) ? {
      value: card.value
    } : {}),
    ...(typeof card.how === 'string' && card.how.trim() ? {
      how: card.how.trim()
    } : {})
  };
}

/** Contact info resolved from a [Name] tag in the user's message. */

/** Task info resolved from a [Label] tag in the user's message. */

/** Pulse template info resolved from a [Name] tag when the user picks a template from the catalog. */

/** Structured references attached by the mobile client when a user @-mentions contacts, tasks, or pulse templates. */

/** Shape accepted by create calls — only the fields the caller controls. */

const TEXT_ROLES = new Set([MessageRole.USER, MessageRole.ASSISTANT, MessageRole.ASSISTANT_STEP, MessageRole.SYSTEM, MessageRole.TOOL_RESULT, MessageRole.THINKING]);
export const isTextMessageRole = role => TEXT_ROLES.has(role);
export const isValidIsaConversationMessage = properties => {
  if (!isValidBase(properties) || !validateProps(properties, ['conversationId'])) {
    return false;
  }
  // Narrow to the loose shape for role-specific discriminated-union checks below.
  const p = properties;
  if (p.role === undefined || p.role === MessageRole.UNKNOWN_MESSAGE_ROLE) {
    return false;
  }
  if (p.role === MessageRole.TOOL_USE) {
    return typeof p.toolName === 'string' && p.toolName.length > 0 && typeof p.toolInput === 'string' && p.toolInput.length > 0;
  }
  if (isTextMessageRole(p.role)) {
    return typeof p.content === 'string';
  }
  return false;
};
//# sourceMappingURL=IsaConversationMessage.js.map